/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useMsal } from "@azure/msal-react";
import { Box, createStyles, makeStyles, Theme, TooltipProps, useMediaQuery } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Tabs from "@material-ui/core/Tabs";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SearchOutlined from "@material-ui/icons/SearchOutlined";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import TabContext from "@material-ui/lab/TabContext";
import TabPanel from "@material-ui/lab/TabPanel";
import useTheme from "@material-ui/styles/useTheme";
import clsx from "clsx";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ReactComponent as ProductIcon } from "../../assets/icons/product-white.svg";
import { ReactComponent as LeapLogo } from "../../assets/images/leap-logo.svg";
import { ReactComponent as TesaLogo } from "../../assets/images/tesa-logo.svg";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import GenericPermissionContext from "../../contexts/GenericPermissionContext";
import { NavigationContext } from "../../contexts/NavigationContext";
import { SpecialContentContext } from "../../contexts/SpecialContentContext";
import { getSidenavMinimized, setSidenavMinimized } from "../../helper/UserSettingsHelper";
import { tesaBlue, tesaBlue3, tesaWhite } from "../../styles/colors";
import StyledTooltip from "../Wrapper/StyledTooltip";
import { StyledVerticalSubTab, StyledVerticalTab } from "./StyledVerticalTabs";

const TAB_HEIGHT_IN_PX = 56;
const SUBTAB_HEIGHT_IN_PX = 32;

function divideWithUnit(val: string, unit: string, divisor: number): string {
  return `${parseInt(val.replace(unit, ""), 10) / divisor}${unit}`;
}

const OptionalTooltip: React.FC<TooltipProps & { show: boolean }> = (props) => {
  const { show, ...innerProps } = props;
  return show ? <StyledTooltip {...innerProps} /> : innerProps.children;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    animatedLabel: {
      height: "auto",
      opacity: 1,
      transition: theme.transitions.create("opacity", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      "&.hidden": {
        height: 0,
        opacity: 0,
        transition: theme.transitions.create("opacity", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    colorWhite: {
      color: tesaWhite,
    },
    content: {
      flex: "1 0 auto",
      width: 0,
      minHeight: "100vh",
      minWidth: theme.pages.request.minWidth,
      "& .LEAP-accessDenied-label-container": {
        transform: `translate(calc(-50% - ${divideWithUnit(theme.navbar.width.std, "px", 2)}), -50%)`,
      },

      "&.minimized": {
        "& .LEAP-accessDenied-label-container": {
          transform: `translate(calc(-50% - ${divideWithUnit(theme.navbar.width.min, "px", 2)}), -50%)`,
        },
      },
    },
    customIndicator: {
      borderBottom: "1rem solid transparent",
      borderRight: "1rem solid white",
      borderTop: "1rem solid transparent",
      height: 0,
      position: "absolute",
      right: 0,
      width: 0,
      zIndex: 200,
      transition: theme.transitions.create("opacity", {
        easing: "cubic-bezier(1, 0, 1, 0.7)",
        duration: theme.transitions.duration.standard,
      }),
      "&.minimized": {
        opacity: 0,
        transition: theme.transitions.create("opacity", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    iconSvg: {
      color: tesaWhite,
      fontSize: "1.5rem",
      height: "1em",
      width: "1em",
      "$&path": {},
    },
    leapLogo: {
      top: 0,
      height: "auto",
      width: "100%",
    },
    logoutContainer: {
      alignSelf: "center",
      alignItems: "center",
      bottom: theme.spacing(12),
      color: tesaWhite,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      fontFamily: "Proxima Nova Bold Condensed",
      fontSize: "0.875rem",
      height: "54px",
      position: "fixed",
      transition: theme.transitions.create("transform", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),

      "&.minimized": {
        transform: `translateY(${theme.spacing(6)}px)`,
      },
    },
    navbarContainer: {
      backgroundColor: tesaBlue,
      display: "flex",
      flexDirection: "column",
      flex: "0 0 auto",
      width: theme.navbar.width.std,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      zIndex: 100,

      "&.minimized": {
        width: theme.navbar.width.min,
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    navbarSticky: {
      position: "sticky",
      top: 0,
    },
    navbarToggle: {
      display: "block",
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: theme.spacing(2.5),

      "&.minimized": {
        marginTop: theme.spacing(0.75),
      },
    },
    spacer: {
      height: "72px",
      transition: theme.transitions.create("height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      "&.minimized": {
        height: "100px",
        transition: theme.transitions.create("height", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    tabPanel: {
      padding: 0,
    },
    tabsRoot: {
      transition: "min-height, 1s",
      "&.error": {
        display: "none",
      },
      "& .MuiTabs-indicator": {
        backgroundColor: tesaWhite,
        left: 0,
        right: "auto",
        width: "3px",
      },
      "&.LEAPTagIndicator-admin": {
        // use !important to override MUI Tabs component inline styles
        "& .MuiTabs-indicator": {
          top: `${3 * TAB_HEIGHT_IN_PX}px !important`,
          height: `${TAB_HEIGHT_IN_PX + 2 * SUBTAB_HEIGHT_IN_PX}px !important`,
        },
        "&.minimized .MuiTabs-indicator": {
          top: `${3 * TAB_HEIGHT_IN_PX}px !important`,
          height: `${3 * TAB_HEIGHT_IN_PX}px !important`,
        },
      },
      "&.LEAPTagIndicator-products": {
        // use !important to override MUI Tabs component inline styles
        "& .MuiTabs-indicator": {
          top: `${2 * TAB_HEIGHT_IN_PX}px !important`,
          height: `${TAB_HEIGHT_IN_PX + 3 * SUBTAB_HEIGHT_IN_PX}px !important`,
        },
        "&.minimized .MuiTabs-indicator": {
          top: `${2 * TAB_HEIGHT_IN_PX}px !important`,
          height: `${4 * TAB_HEIGHT_IN_PX}px !important`,
        },
      },
    },
    tesaLogo: {
      alignSelf: "center",
      position: "fixed",
      bottom: theme.spacing(5),
      transition: theme.transitions.create("height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),

      "&.minimized": {
        width: "45px",
        height: "auto",
        transition: theme.transitions.create("height", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    version: {
      alignSelf: "center",
      position: "fixed",
      bottom: theme.spacing(0.5),
      color: tesaBlue3,
    },
  }),
);

const determineInitialTab = (
  initialTabPath: string,
  allowedAccessProducts: boolean,
  allowedAccessAdmin: boolean,
): string | false => {
  let initialTab: string | false = initialTabPath ? `/${initialTabPath}` : "/requests";
  if (
    (!allowedAccessProducts && initialTabPath === "products") ||
    (!allowedAccessAdmin && initialTabPath === "admin")
  ) {
    initialTab = false;
  }
  return initialTab;
};

const getSubTab = (tabInit: string, subTabInit: string): string => {
  const hasSubTabs = {
    products: true,
    "/products": true,
    admin: true,
    "/admin": true,
  };
  return subTabInit && tabInit in hasSubTabs ? `/${subTabInit}` : "";
};

interface VerticalNavbarProps {
  error?: boolean;
}

const VerticalNavbar: React.FC<React.PropsWithChildren<VerticalNavbarProps>> = (props) => {
  const { children } = props;
  const classes = useStyles(props);
  const { accessAdmin, accessProducts, extendedAdminRights, viewLaboratoryTab } = useContext(GenericPermissionContext);
  const { instance } = useMsal();
  const { dirtyFlag, preventNav } = useContext(NavigationContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const history = useHistory();
  const { pathname } = useLocation();
  const theme = useTheme<Theme>();
  const tabInit = pathname.split("/")[1];
  const subTabInit = pathname.split("/")[2];
  const sidenavAutoFullsize = useMediaQuery(theme.breakpoints.up("mdLg"));
  const sidenavMinimized = getSidenavMinimized();
  const [currentTab, setCurrentTab] = useState<string | false>(
    determineInitialTab(tabInit, accessProducts, accessAdmin),
  );
  const [currentSubTab, setCurrentSubTab] = useState(getSubTab(tabInit, subTabInit));
  const [minimized, setMinimized] = useState<boolean>(
    sidenavMinimized === null ? !sidenavAutoFullsize : sidenavMinimized,
  );
  const { updateXmasSpecialDialogOpen } = useContext(SpecialContentContext);

  const stringifiedEnvObject = localStorage.getItem("envObject");
  let version = "";
  if (stringifiedEnvObject) {
    const envObject = JSON.parse(stringifiedEnvObject);
    version = `v ${envObject.deployment_version}`;
  }

  const handleToggleMinimize = () => {
    updateXmasSpecialDialogOpen(true);
    setMinimized((prev) => {
      setSidenavMinimized(!prev);
      return !prev;
    });
  };

  useEffect(() => {
    if (sidenavMinimized === null) {
      setMinimized(!sidenavAutoFullsize);
    }
  }, [sidenavAutoFullsize]);

  const navigateOnTabChange = (tab: string, subTab: string = "") => {
    localStorage.removeItem("recentDialog");
    history.push(`${tab}${subTab}`);
  };

  const changeTab = (tab: string, subTab: string, navigate: boolean = true) => {
    if (dirtyFlag) {
      preventNav?.()
        .then(() => {
          setCurrentTab(tab);
          setCurrentSubTab(subTab);
          if (navigate) navigateOnTabChange(tab, subTab);
        })
        .catch((err) => {
          if (err) {
            handleHttpError(err);
          }
        });
    } else {
      setCurrentTab(tab);
      setCurrentSubTab(subTab);
      if (navigate) navigateOnTabChange(tab, subTab);
    }
  };

  useEffect(() => {
    if (pathname.includes("/requests")) {
      setCurrentTab("/requests");
    }
  }, [pathname]);
  return (
    <div className={classes.root}>
      <div
        className={clsx(classes.navbarContainer, {
          minimized,
        })}
      >
        <Box className={classes.navbarSticky}>
          <LeapLogo className={classes.leapLogo} />
          <Box
            className={clsx(classes.spacer, {
              minimized,
            })}
          />
          <TabContext value={currentTab || ""}>
            <Tabs
              centered
              classes={{ root: classes.tabsRoot }}
              className={clsx({
                "LEAPTagIndicator-admin": currentTab === "/admin",
                "LEAPTagIndicator-products": currentTab === "/products",
                minimized,
              })}
              orientation="vertical"
              value={currentTab}
            >
              <StyledVerticalTab
                fullWidth
                className={clsx({ minimized })}
                id="/requests"
                icon={
                  <OptionalTooltip placement="right" show={minimized} title="Requests">
                    <AssignmentIcon />
                  </OptionalTooltip>
                }
                label={minimized ? undefined : <span>Requests</span>}
                onClick={() => {
                  changeTab("/requests", "");
                }}
                value="/requests"
              />
              <StyledVerticalTab
                className={clsx({ minimized })}
                id="/search"
                icon={
                  <OptionalTooltip placement="right" show={minimized} title="Archive">
                    <SearchOutlined viewBox="0.5 2 20 20" />
                  </OptionalTooltip>
                }
                label={minimized ? undefined : <span>Archive</span>}
                onClick={() => {
                  changeTab("/search", "");
                }}
                value="/search"
              />
              {accessProducts && [
                <StyledVerticalTab
                  className={clsx({ minimized })}
                  id="/products"
                  icon={
                    <OptionalTooltip placement="right" show={minimized} title="Products">
                      <ProductIcon className={classes.iconSvg} />
                    </OptionalTooltip>
                  }
                  key="products-tab"
                  label={minimized ? undefined : <span>Products</span>}
                  onClick={() => {
                    changeTab("/products", "/prototype");
                  }}
                  value="/products"
                />,
                <TabPanel className={classes.tabPanel} key="products-subtabs" value="/products">
                  <Tabs value={currentSubTab} orientation="vertical" centered>
                    <StyledVerticalSubTab
                      className={clsx({
                        minimized,
                      })}
                      id="/prototype"
                      label={
                        <OptionalTooltip placement="right" show={minimized} title="Prototype">
                          <span>{minimized ? "P" : "Prototype"}</span>
                        </OptionalTooltip>
                      }
                      onClick={() => {
                        changeTab("/products", "/prototype");
                      }}
                      value="/prototype"
                    />
                    <StyledVerticalSubTab
                      className={clsx({
                        minimized,
                      })}
                      id="/competitor"
                      label={
                        <OptionalTooltip placement="right" show={minimized} title="Competitor">
                          <span>{minimized ? "C" : "Competitor"}</span>
                        </OptionalTooltip>
                      }
                      onClick={() => {
                        changeTab("/products", "/competitor");
                      }}
                      value="/competitor"
                    />
                    <StyledVerticalSubTab
                      className={clsx({
                        "indicator-minimized": minimized && currentTab === "/products",
                        minimized,
                      })}
                      id="/unknown"
                      label={
                        <OptionalTooltip placement="right" show={minimized} title="Unknown">
                          <span>{minimized ? "U" : "Unknown"}</span>
                        </OptionalTooltip>
                      }
                      onClick={() => {
                        changeTab("/products", "/unknown");
                      }}
                      value="/unknown"
                    />
                  </Tabs>
                </TabPanel>,
              ]}
              {viewLaboratoryTab && [
                <StyledVerticalTab
                  className={clsx({ minimized })}
                  id="/admin"
                  icon={
                    <OptionalTooltip placement="right" show={minimized} title="Admin">
                      <SupervisorAccountIcon className={classes.iconSvg} />
                    </OptionalTooltip>
                  }
                  key="admin-tab"
                  label={minimized ? undefined : <span>Admin</span>}
                  onClick={() => {
                    const targetTab = accessAdmin ? "/statistics" : "/labs";
                    changeTab("/admin", targetTab);
                  }}
                  value="/admin"
                />,
                <TabPanel className={classes.tabPanel} key="admin-subtabs" value="/admin">
                  <Tabs value={currentSubTab} orientation="vertical" centered>
                    {accessAdmin && (
                      <StyledVerticalSubTab
                        className={clsx({
                          minimized,
                        })}
                        id="/statistics"
                        label={
                          <OptionalTooltip placement="right" show={minimized} title="Statistics">
                            <span>{minimized ? "STA" : "Statistics"}</span>
                          </OptionalTooltip>
                        }
                        onClick={() => {
                          changeTab("/admin", "/statistics");
                        }}
                        value="/statistics"
                      />
                    )}
                    {extendedAdminRights && accessAdmin && (
                      <StyledVerticalSubTab
                        className={clsx({
                          minimized,
                        })}
                        id="/settings"
                        label={
                          <OptionalTooltip placement="right" show={minimized} title="Settings">
                            <span>{minimized ? "SET" : "Settings"}</span>
                          </OptionalTooltip>
                        }
                        onClick={() => {
                          changeTab("/admin", "/settings");
                        }}
                        value="/settings"
                      />
                    )}
                    <StyledVerticalSubTab
                      className={clsx({
                        minimized,
                      })}
                      id="/labs"
                      label={
                        <OptionalTooltip placement="right" show={minimized} title="Labs">
                          <span>{minimized ? "LAB" : "Labs"}</span>
                        </OptionalTooltip>
                      }
                      onClick={() => {
                        changeTab("/admin", "/labs");
                      }}
                      value="/labs"
                    />

                    <StyledVerticalSubTab
                      className={clsx({
                        minimized,
                      })}
                      id="/report-templates"
                      label={
                        <OptionalTooltip placement="right" show={minimized} title="Templates">
                          <span>{minimized ? "TMPTES" : "Templates"}</span>
                        </OptionalTooltip>
                      }
                      onClick={() => {
                        changeTab("/admin", "/report-templates");
                      }}
                      value="/report-templates"
                    />
                  </Tabs>
                </TabPanel>,
              ]}
              <StyledVerticalTab
                className={clsx({ minimized })}
                id="/logout"
                icon={
                  <OptionalTooltip placement="right" show={minimized} title="Log out">
                    <ExitToAppIcon className={classes.iconSvg} />
                  </OptionalTooltip>
                }
                label={minimized ? undefined : <span>LOG OUT</span>}
                onClick={() => {
                  setCurrentTab("/logout");
                  if (dirtyFlag) {
                    preventNav?.()
                      .then(() => instance.logout())
                      .catch((err) => {
                        if (err) {
                          handleHttpError(err);
                        }
                      });
                  } else {
                    instance.logout();
                  }
                }}
                value="/logout"
              />
            </Tabs>
          </TabContext>
          <IconButton className={clsx(classes.navbarToggle, { minimized })} onClick={handleToggleMinimize}>
            {minimized ? (
              <ChevronRightIcon className={classes.colorWhite} viewBox="4 4 16 16" />
            ) : (
              <ChevronLeftIcon className={classes.colorWhite} viewBox="4 4 16 16" />
            )}
          </IconButton>
        </Box>
        <TesaLogo className={clsx(classes.tesaLogo, { minimized })} />
        <Box className={classes.version}>{version}</Box>
      </div>
      <div className={clsx(classes.content, { minimized })}>{children}</div>
    </div>
  );
};

export default VerticalNavbar;
